import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  ScheduleRefreshConfig,
  ScheduleRefreshErrors,
  Weekday,
} from "./ScheduleRefreshModal.types";
import {
  isScheduleRefreshValid,
  validateScheduleRefresh,
} from "./scheduleRefreshValidation";

/** Dropdown identifiers — only one is open at a time. */
export type ScheduleRefreshDropdownKey =
  | "unit"
  | "hour"
  | "timezone"
  | "incrementalColumn";

const DEFAULTS: ScheduleRefreshConfig = {
  every: 1,
  unit: "week",
  repeatOn: ["mon", "tue", "wed", "thu", "fri"],
  startsOn: "",
  refreshHour: 10,
  timezone: "UTC",
  incrementalEnabled: false,
  incrementalColumn: null,
};

const merge = (
  initial: Partial<ScheduleRefreshConfig> | undefined,
): ScheduleRefreshConfig => ({ ...DEFAULTS, ...(initial ?? {}) });

export interface UseScheduleRefreshArgs {
  /** Reset state to this initial value when `open` flips false→true. */
  initialValue?: Partial<ScheduleRefreshConfig>;
  /** Modal open flag — used to reset state on each open. */
  open: boolean;
}

export interface UseScheduleRefreshResult {
  config: ScheduleRefreshConfig;
  errors: ScheduleRefreshErrors;
  /** True after the user has tried to submit — controls error display. */
  showErrors: boolean;
  /** True when the config passes validation. */
  isValid: boolean;
  /** Currently-open dropdown (one at a time). */
  openDropdown: ScheduleRefreshDropdownKey | null;
  patch: (next: Partial<ScheduleRefreshConfig>) => void;
  toggleWeekday: (day: Weekday) => void;
  openDropdownKey: (key: ScheduleRefreshDropdownKey) => void;
  closeDropdown: () => void;
  /** Marks form as touched (submit attempt). Returns whether config is valid. */
  attemptSubmit: () => boolean;
}

/**
 * State + validation for the Schedule Refresh modal. Stateless from a parent
 * perspective — the modal owns this hook and only exposes onSubmit upward.
 */
export const useScheduleRefresh = ({
  initialValue,
  open,
}: UseScheduleRefreshArgs): UseScheduleRefreshResult => {
  const [config, setConfig] = useState<ScheduleRefreshConfig>(() =>
    merge(initialValue),
  );
  const [showErrors, setShowErrors] = useState(false);
  const [openDropdown, setOpenDropdown] =
    useState<ScheduleRefreshDropdownKey | null>(null);

  // Reset state every time the modal re-opens so stale form state from a
  // previous session doesn't bleed in.
  useEffect(() => {
    if (open) {
      setConfig(merge(initialValue));
      setShowErrors(false);
      setOpenDropdown(null);
    }
  }, [open, initialValue]);

  const errors = useMemo(() => validateScheduleRefresh(config), [config]);
  const isValid = useMemo(() => isScheduleRefreshValid(config), [config]);

  const patch = useCallback(
    (next: Partial<ScheduleRefreshConfig>) => {
      setConfig((prev) => ({ ...prev, ...next }));
    },
    [],
  );

  const toggleWeekday = useCallback((day: Weekday) => {
    setConfig((prev) => {
      const has = prev.repeatOn.includes(day);
      return {
        ...prev,
        repeatOn: has
          ? prev.repeatOn.filter((d) => d !== day)
          : [...prev.repeatOn, day],
      };
    });
  }, []);

  const openDropdownKey = useCallback(
    (key: ScheduleRefreshDropdownKey) => setOpenDropdown(key),
    [],
  );

  const closeDropdown = useCallback(() => setOpenDropdown(null), []);

  const attemptSubmit = useCallback((): boolean => {
    setShowErrors(true);
    return isScheduleRefreshValid(config);
  }, [config]);

  return {
    config,
    errors,
    showErrors,
    isValid,
    openDropdown,
    patch,
    toggleWeekday,
    openDropdownKey,
    closeDropdown,
    attemptSubmit,
  };
};
